import { useRef } from "react";
import { useForm } from "react-hook-form";

import { SelectBuyerList } from "components/form_components/BuyerListSelect";
import { BuyerSelect } from "components/form_components/BuyerSelect";
import { BuyerTypeSelect } from "components/form_components/BuyerTypeSelect";
import { Radio, Select } from "components/form_components/Inputs";
import { SupplierSelect } from "components/form_components/SupplierSelect";
import RedactedWrapper from "lib/core_components/RedactedWrapper";
import { Framework_Lots } from "lib/generated/app-service-gql/graphql";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import FilterFormTitle from "../filter_form/filter_form_title/FilterFormTitle";
import { useSuppliersTracking } from "./tracking";
import { SupplierFilters } from "./utils";

import css from "./FilterForm.module.scss";

export type FilterSections = "suppliers" | "supplierGuids" | "buyers" | "lots" | "signals";

export type FilterFormProps = {
  onChange: (filters: SupplierFilters) => void;
  filters: SupplierFilters;
  closeDrawer: () => void;
  hiddenSections?: FilterSections[];
  frameworkLots?: Framework_Lots[];
};

export function FilterForm({
  onChange,
  filters,
  closeDrawer,
  hiddenSections = ["lots"],
  frameworkLots = [],
}: FilterFormProps) {
  const { control, watch, getValues, trigger } = useForm<typeof filters>({
    defaultValues: filters,
  });
  const previousFilters = useRef(filters);
  const tracking = useSuppliersTracking();

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (d) => {
      tracking.filterFormChanged(previousFilters.current, d);
      previousFilters.current = d;
      onChange(d);
    },
  });

  return (
    <>
      <FilterFormTitle onClose={closeDrawer} />
      <form aria-label="Supplier filters">
        <div className={css.formContent}>
          {hiddenSections.includes("suppliers") ? null : (
            <>
              <h3 className={css.sectionTitle}>Suppliers</h3>
              <div className={css.filterSection}>
                <SupplierSelect name="guids" label="Supplier Guids" control={control} />
                <RedactedWrapper
                  redactContent={
                    <Radio
                      label="SME Supplier?"
                      name={"isSme"}
                      control={control}
                      disabled
                      options={[
                        { label: "Show all suppliers", value: "" },
                        { label: "Show only SMEs", value: true },
                        { label: "Hide SMEs", value: false },
                      ]}
                      optionType="default"
                      direction="vertical"
                    />
                  }
                  requiredDataType="SUPPLIERS"
                  contextSource="Supplier filter"
                >
                  <Radio
                    label="SME Supplier?"
                    name={"isSme"}
                    control={control}
                    options={[
                      { label: "Show all suppliers", value: "" },
                      { label: "Show only SMEs", value: true },
                      { label: "Hide SMEs", value: false },
                    ]}
                    optionType="default"
                    direction="vertical"
                    defaultValue=""
                  />
                </RedactedWrapper>
              </div>
            </>
          )}
          {hiddenSections.includes("buyers") ? null : (
            <>
              <h3 className={css.sectionTitle}>Buyers</h3>
              <div className={css.filterSection}>
                <BuyerSelect
                  name="buyerIds"
                  label="Buyers"
                  control={control}
                  mode="multiple"
                  placeholder="Search buyers..."
                  allowClear
                />
                <SelectBuyerList
                  name="buyerLists"
                  label="Buyer lists"
                  control={control}
                  placeholder="Select..."
                  mode="multiple"
                  allowClear
                />
                <BuyerTypeSelect
                  name="buyerCategories"
                  control={control}
                  multiple
                  label="Buyer type"
                  placeholder="Select..."
                  allowClear
                />
              </div>
            </>
          )}

          {hiddenSections.includes("lots") && frameworkLots ? null : (
            <>
              <h3 className={css.sectionTitle}>Lots</h3>
              <div className={css.filterSection} aria-label="Lots filter section">
                <Select
                  name="lotIds"
                  control={control}
                  label="Lot name"
                  placeholder="Select lots..."
                  options={frameworkLots.map((lot, index) => ({
                    key: index,
                    label: `${lot.id}: ${lot.title}`,
                    value: lot.id,
                  }))}
                  mode="multiple"
                  optionFilterProp="label"
                  allowClear
                />
              </div>
            </>
          )}
        </div>
      </form>
    </>
  );
}
